<template>
  <div>
    <small class="d-block mb-3">
      Structured Data
      <b-icon-info-circle v-b-tooltip.hover="`Relevant structured (i.e. schema) data that may appear on Google results. Checkmarks indicate the data is present in SERPs.`"/>
    </small>
    <div v-for="feature in jsonLdFeatures" :key="feature.id" class="word-list-item d-flex d-flex justify-content-between align-items-center mb-1">
        <div class="item flex-grow-1 d-flex align-items-center">
          <div 
            class="text-muted" 
            :style="{maxWidth:'calc(100% - 24px)'}" 
          >
          {{ feature.title }}
          <small><b-icon-info-circle v-if="feature.tooltip" v-b-tooltip.hover="feature.tooltip" /></small>
          </div>
        </div>
        <div class="mr-2">
          <small v-if="feature.showValue">{{ feature.value }}</small>
          <b-icon-check-circle-fill v-else-if="feature.serp" variant="success" v-b-tooltip.hover="'Present in SERPs'" />
        </div>
      </div>
  </div>
</template>

<script>
import {get} from 'lodash'
export default {
  name: 'PageSerp',
  props: ['page'],
  components: {},
  data: () => {
    return {
      features: {
        language: {
          title: 'Language',
          showValue: true,
          tooltip: () => 'Set by the html lang attribute or content-language header'
        },
        rating:  {
          title: 'Rating',
          tooltip: e => e.serp ? e.serp : (e.value * 5).toPrecision(1),
        },
        reviews:  {
          title: 'Reviews',
          tooltip: e => e.serp ? e.serp : e.value,
        },
        votes:  {
          title: 'Votes',
          tooltip: e => e.serp ? e.serp : e.value,
        },
        review_by:  {
          title: 'Review By',
          tooltip: e => e.serp ? e.serp : e.value,
        },
        price:  {
          title: 'Price',
          tooltip: e => e.serp ? e.serp : e.value,
        },
        toc:  {
          title: 'Table of Contents',
          tooltip: e => {
            return (e.serp || e.value)
              .slice(0, 3)
              .map(e => e.text.slice(0, 30) + '…')
              .join(', ')
          },
        },
        faqs:  {
          title: 'FAQs',
          tooltip: e => {
            return (e.serp || e.value)
              .slice(0, 3)
              .map(e => e.q.slice(0, 30) + '…')
              .join(', ')
          },
        },
        thumbnail:  {
          title: 'Thumbnail',
          tooltip: e => e.serp ? e.serp : e.value.split('/').pop(),
        },
        duration:  {
          title: 'Duration',
          tooltip: e => e.serp ? e.serp : e.value,
        },
        favicon:  {
          title: 'Favicon',
          tooltip: e => e.serp ? e.serp : e.value,
        },
        date:  {
          title: 'Date',
          tooltip: e => e.serp ? e.serp : e.value.toLocaleDateString(),
        },
        not_mobile:  {
          title: 'Not Mobile Friendly',
          tooltip: e => e.serp ? 'Warning shown on SERP' : false,
        },
      }
    }
  },

  computed: {
    jsonLdFeatures() {
      const response = {}
      if(!this.page.json_ld) {
        return response
      }
      if(this.page.language) {
        response.language = this.page.language
      }
      let graph = get(this.page.json_ld, '@graph', this.page.json_ld)
      let article = graph
      if(Array.isArray(graph)) {
        article = graph.find(e => ['Article', 'WebPage', 'BlogPosting'].includes(e['@type']))
      } else {
        graph = [graph]
      }
      if(get(article, 'datePublished')) {
        response.date = new Date(article.datePublished)
      }
      
      const image = graph.find(e => ['PrimaryImage'].includes(e['@type']))
      if(image) {
        response.thumbnail = image.url
      }
      
      const review = graph.find(e => ['Review'].includes(e['@type']))
      if(review) {
        const author = review.author['@id'] ? graph.find(e => e['@id'] == review.author['@id']) : review.author
        const rating = review.rating['@id'] ? graph.find(e => e['@id'] == review.rating['@id']) : review.rating
        if(author?.name) {
          response.review_by = get(author, 'name')
        }
        if(rating?.bestRating) {
          response.rating = rating.ratingValue / (rating.bestRating - rating.worstRating)
        }
      }

      const faqs = graph.find(e => ['FaqPage'].includes(e['@type']))
      if(faqs && faqs.mainEntity) {
        response.faqs = faqs.mainEntity.map(e => ({
          q: e.name, 
          a: get(e, 'acceptedAnswer.text')
        }))
      }

      const reviews = graph.find(e => e.aggregateRating)
      if(reviews) {
        response.reviews = reviews.aggregateRating.reviewCount
        response.rating = rating.ratingValue / (rating.bestRating - rating.worstRating)
      }

      const product = graph.find(e => ['Product'].includes(e['@type']))
      if(product && product.offer) {
        const offer = get(product, 'offer.[0]') || get(product, 'offer')
        const price = offer.priceSpecification || offer
        if(price) {
          response.price = _.toNumber(price.price).toLocaleString('en-US', { style: 'currency', currency: price.priceCurrency});
        }
      }

      const video = graph.find(e => ['VideoObject'].includes(e['@type']))
      if(video) {
        response.duration = video.duration.startsWith('T') ? video.duration.split(/[a-z]/i).filter(e => e.length).join(':').replace(/^0:/, '') : video.duration
        response.thumbnail = video.thumbnailUrl
        if(video.uploadDate) {
          response.date = new Date(video.uploadDate)
        }
      }

      return Object.keys(this.features)
        .map(id => ({
          id,
          value: response[id],
          serp: get(this.page.serp_result, ['data', id]),
        }))
        .filter(e => e.value || e.serp)
        .map(e => ({
          ...e,
          ...this.features[e.id],
          tooltip: this.features[e.id].tooltip(e),
        }))
    },
  },
}
</script>
